import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { MaskPipe } from 'ngx-mask';
import Utils from 'src/app/helpers/utils';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import {
  BreadcrumbService,
  BreadcrumbItem,
} from 'src/app/services/breadcrumb/breadcrumb.service';
import { DRAFTS_PAGE_URL, draftsTabUrl } from 'src/app/data/draft-tabs';

@Component({
  selector: 'app-dashboard-drafts-models-list',
  templateUrl: './dashboard-drafts-models-list.component.html',
  styleUrls: ['./dashboard-drafts-models-list.component.scss'],
})
export class DashboardDraftsModelsListComponent implements OnInit {
  form = this.formBuilder.group({
    name: [null, []],
    type: [null, []],

    initial_date: [
      null,
      [
        Utils.validateWhenNotEmpty(Utils.validateDateValid),
        Utils.validateWhenNotEmpty(Utils.validateMaxMinToday),
      ],
    ],
    final_date: [
      null,
      [
        Utils.validateWhenNotEmpty(Utils.validateDateValid),
        Utils.validateWhenNotEmpty(Utils.validateMaxMinToday),
      ],
    ],
    status: [null, []],

    roles: [[], []],
    agents: [[], []],
    coobligation: [null, []],

    content: [null, []],
  });

  applicableItems = [];

  coobligationItems = [
    { label: 'Sem coobrigação', value: false },
    { label: 'Com coobrigação', value: true },
  ];

  agentItems = [];
  typeItems = [];
  statusItems = [
    { label: 'Ativo', value: 'ativo' },
    { label: 'Rascunho', value: 'rascunho' },
    { label: 'Erro', value: 'erro' },
    { label: 'Inativo', value: 'inativo' },
  ];

  roles: Role[] = [];
  agents: Agent[] = [];
  types: any[] = [];

  drafts: DraftDocument[] = [];
  loadingDrafts: boolean = true;

  showFilterModal = false;
  warningModal: boolean = false;
  /**
   * Padrão de Filtros:
   * - hasFilters só deve ser true APÓS aplicar os filtros (método submitForm())
   * - NÃO atualizar hasFilters no valueChanges do form
   * - hasFilters deve ser atualizado apenas quando:
   *   1. submitForm() executar com sucesso (após a requisição)
   *   2. resetForm() for chamado (define como false)
   *   3. toggleFilterModal() quando há filtros salvos no storage
   */
  hasFilters = false;
  appliedFilters: Array<{ label: string; value: string }> = [];

  permissionObj = {
    canAdd: true,
    canChange: true,
    canEdit: true,
    canFilter: true,
  };

  canViewVariables: boolean = false;
  errorVariables: InactiveVariable[] = [];
  loadingVariables: boolean = false;
  selectedDraft: string = '';

  count: number = 0;
  offset: number = 0;
  results_per_page = 0;

  // Propriedades para o warning modal
  showStatusModal = false;
  selectedModel: any = null;
  isActive: boolean = false;

  constructor(
    private router: Router,
    private apiService: ApiService,
    private toastService: ToastService,
    private formBuilder: FormBuilder,
    private toast: ToastService,
    private authService: AuthService,
    private maskPipe: MaskPipe,
    private breadcrumbService: BreadcrumbService
  ) {}

  ngOnInit(): void {
    this.authService.canViewPage(['de_can_view_drafts']);
    this.canViewVariables = this.authService.verifyPermission(
      'de_can_view_variables'
    );

    this.setPermissions();
    this.setBreadcrumb();

    this.fetchData();
  }

  async fetchData() {
    let requests = [this.fetchRoles(), this.fetchAgents(), this.fetchTypes()];

    // Construir filtros a partir do formulário (se houver valores)
    const filters: any = {};
    const values = this.form.value;

    Object.entries(values).forEach(([key, value]: any) => {
      if (value !== null && value !== '' && value !== undefined) {
        // Arrays vazios não devem ser incluídos
        if (Array.isArray(value) && value.length === 0) {
          return;
        }
        // Verificar se o campo tem erro de validação antes de incluir
        const control = this.form.get(key);
        if (control && control.valid) {
          let newValue = value;
          if (key === 'initial_date' || key === 'final_date') {
            newValue = value.split('/').reverse().join('-');
          }
          filters[key] = newValue;
        }
      }
    });

    // Se houver filtros válidos, aplicar
    if (Object.keys(filters).length > 0) {
      this.hasFilters = true;
      this.generateFilterTags(this.form.value);
      requests.push(this.fetchDrafts(1, filters));
    } else {
      this.hasFilters = false;
      this.appliedFilters = [];
      requests.push(this.fetchDrafts());
    }

    await Promise.all(requests);
    this.loadingDrafts = false;
  }

  async fetchRoles() {
    try {
      const response = await this.apiService.get<Role[]>({
        route: 'api/roles',
        token: true,
      });

      this.roles = response;

      const acceptedApplicables = ['shareholder', 'assignor', 'provider'];

      const mappedData = response
        .filter((item) => acceptedApplicables.includes(item.applicable))
        .map((item) => ({
          label: `${item.applicable_display} - ${item.name}`,
          value: item.id,
        }))
        .sort((a, b) => {
          if (a.label < b.label) return -1;
          if (a.label > b.label) return 1;
          return 0;
        });

      this.applicableItems = mappedData;
    } catch (error) {
      console.log(error);

      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao carregar os dados de papeis.'
      );
    }
  }

  async fetchAgents() {
    try {
      const response = await this.apiService.get<Agent[]>({
        route: 'api/registration/agent',
        token: true,
      });

      this.agents = response;

      const mappedData = response
        .map((item) => ({
          label: item.name,
          value: item.id,
        }))
        .sort((a, b) => {
          if (a.label < b.label) return -1;
          if (a.label > b.label) return 1;
          return 0;
        });

      this.agentItems = mappedData;
    } catch (error) {
      console.log(error);

      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao carregar os dados de agentes.'
      );
    }
  }

  async fetchTypes() {
    try {
      const response = await this.apiService.get({
        route: 'api/document-type/options/',
        token: true,
        params: { status: 'active' },
      });

      const types = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.results)
            ? response.results
            : [];

      this.types = types;

      this.typeItems = types
        .map((item) => ({
          label: item.name ?? item.label ?? item.type_document,
          value: item.id ?? item.value,
        }))
        .sort((a, b) => {
          if (a.label < b.label) return -1;
          if (a.label > b.label) return 1;
          return 0;
        });
    } catch (error) {
      console.log(error);

      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao carregar os dados de tipos de documento.'
      );
    }
  }

  async fetchDrafts(page = 1, params = {}) {
    const newParams = params;

    Object.keys(newParams).forEach((key) => {
      if (typeof newParams[key] === 'boolean') {
        newParams[key] = String(newParams[key]);
      }
    });

    try {
      const { count, results, offset, results_per_page } =
        await this.apiService.get({
          route: 'api/draft/',
          token: true,
          params: { page, ...newParams },
        });

      this.drafts = results;
      this.showFilterModal = false;
      this.count = count;
      this.offset = offset;
      this.results_per_page = results_per_page;
    } catch (error) {
      this.toastService.show('error', 'Erro', 'Falha ao carregar as minutas');
    }
  }

  redirectToForm(id?: number) {
    this.router.navigateByUrl(`/app/drafts/models/${id ?? 'new'}`);
  }

  formatFinalDate(value?: string): string {
    if (!value) {
      return '-';
    }

    const dateOnly = String(value).split('T')[0];

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
      return dateOnly.split('-').reverse().join('/');
    }

    return dateOnly;
  }

  renderDraftRoles(draftRoles: unknown): string {
    if (!Array.isArray(draftRoles) || !draftRoles.length) {
      return '-';
    }

    const labels = draftRoles
      .map((role) => {
        if (typeof role === 'number' || typeof role === 'string') {
          return this.getRoleLabel(Number(role));
        }

        if (role && typeof role === 'object') {
          const item = role as {
            id?: number;
            name?: string;
            applicable_display?: string;
          };

          if (item.applicable_display && item.name) {
            return `${item.applicable_display} - ${item.name}`;
          }

          if (item.name) {
            return item.name;
          }

          if (item.id) {
            return this.getRoleLabel(item.id);
          }
        }

        return null;
      })
      .filter((label): label is string => Boolean(label));

    return labels.length ? labels.join(', ') : '-';
  }

  private getRoleLabel(id: number): string | null {
    const role = this.roles.find((item) => item.id === id);

    if (!role) {
      return null;
    }

    return role.applicable_display
      ? `${role.applicable_display} - ${role.name}`
      : role.name;
  }

  toggleModal(show: boolean) {
    if (show) {
      this.toggleFilterModal();
    } else {
      this.showFilterModal = false;
    }
  }

  verifyIdIsApplicable(field: string, applicable: string) {
    const fieldValue = this.form.controls[field].value ?? [];

    const roles = this.roles.filter((item) => fieldValue.includes(item.id));
    return roles.some((item) => item.applicable === applicable);
  }

  generateFilterTags(formValues: any) {
    this.appliedFilters = [];

    const fieldLabels: { [key: string]: string } = {
      name: 'Nome da minuta',
      type: 'Tipo de documento',
      status: 'Status',
      initial_date: 'Início da vigência',
      final_date: 'Fim da vigência',
      roles: 'Aplicável aos papeis',
      agents: 'Aplicável aos prestadores',
      coobligation: 'Coobrigação',
    };

    Object.entries(formValues).forEach(([key, value]: any) => {
      if (value !== null && value !== '' && value !== undefined) {
        let displayValue = value;

        // Tratamento especial para arrays (múltipla seleção)
        if (Array.isArray(value) && value.length > 0) {
          if (key === 'roles') {
            displayValue = value
              .map((id: number) => {
                const role = this.applicableItems.find((r) => r.value === id);
                return role ? role.label : id;
              })
              .join(', ');
          } else if (key === 'agents') {
            displayValue = value
              .map((id: number) => {
                const agent = this.agentItems.find((a) => a.value === id);
                return agent ? agent.label : id;
              })
              .join(', ');
          } else {
            displayValue = value.join(', ');
          }
        }

        // Tratamento especial para status
        if (key === 'status') {
          const statusOption = this.statusItems.find((s) => s.value === value);
          displayValue = statusOption ? statusOption.label : value;
        }

        // Tratamento especial para tipo
        if (key === 'type') {
          const typeOption = this.typeItems.find((t) => t.value === value);
          displayValue = typeOption ? typeOption.label : value;
        }

        // Tratamento especial para coobrigação
        if (key === 'coobligation') {
          const coobligationOption = this.coobligationItems.find(
            (c) => c.value === value
          );
          displayValue = coobligationOption ? coobligationOption.label : value;
        }

        // Tratamento especial para datas
        if (key === 'initial_date' || key === 'final_date') {
          displayValue = value;
        }

        if (fieldLabels[key]) {
          // Combinar label e valor no formato "label: valor"
          const combinedLabel = `${fieldLabels[key]}: ${String(displayValue)}`;
          this.appliedFilters.push({
            label: combinedLabel,
            value: String(displayValue),
          });
        }
      }
    });
  }

  toggleFilterModal() {
    this.showFilterModal = !this.showFilterModal;

    if (this.showFilterModal) {
      // Se houver filtros aplicados, restaurar no formulário
      if (this.hasFilters && this.appliedFilters.length > 0) {
        // Os valores já estão no formulário, apenas garantir que as tags estão atualizadas
        this.generateFilterTags(this.form.value);
      } else {
        this.hasFilters = false;
        this.appliedFilters = [];
      }
    }
  }

  async resetForm() {
    this.loadingDrafts = true;
    this.showFilterModal = false;
    this.hasFilters = false;
    this.appliedFilters = [];
    this.form.reset();
    await this.fetchDrafts();
    this.loadingDrafts = false;
  }

  renderDate(date: string) {
    return date.split('-').reverse().join('/');
  }

  private buildDraftFilters(formValues: Record<string, unknown>) {
    const payload: Record<string, unknown> = {};

    Object.entries(formValues).forEach(([key, value]) => {
      if (value === null || value === '' || value === undefined) {
        return;
      }

      const control = this.form.get(key);
      if (!control || control.invalid) {
        return;
      }

      if (Array.isArray(value) && value.length === 0) {
        return;
      }

      if (key === 'content') {
        return;
      }

      if (key === 'initial_date' || key === 'final_date') {
        payload[key] = String(value).split('/').reverse().join('-');
        return;
      }

      payload[key] = value;
    });

    return payload;
  }

  async submitForm() {
    // Verificar se o formulário é válido antes de processar
    if (this.form.invalid) {
      // Marcar todos os campos como touched para mostrar erros
      Object.keys(this.form.controls).forEach((key) => {
        this.form.get(key)?.markAsTouched();
      });
      return;
    }

    try {
      this.loadingDrafts = true;

      const payload = this.buildDraftFilters(this.form.value);

      await this.fetchDrafts(1, payload);

      // ✅ IMPORTANTE: hasFilters só é atualizado após aplicar os filtros com sucesso
      this.hasFilters = Object.keys(payload).length > 0;

      // ✅ Gerar as tags dos filtros aplicados
      this.generateFilterTags(this.form.value);

      this.showFilterModal = false;
    } catch (error) {
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao fazer a filtragem.');
    }
    this.loadingDrafts = false;
  }

  setPermissions() {
    const permissions = Object.keys(this.permissionObj).map(
      (key) => `de_can_${key.split('can').join('').toLowerCase()}_drafts`
    );

    permissions.forEach((permission, idx) => {
      this.permissionObj[Object.keys(this.permissionObj)[idx]] =
        this.authService.verifyPermission(permission);
    });
  }

  setBreadcrumb(): void {
    const canViewDrafts =
      this.authService.verifyPermission('de_can_view_drafts');
    const breadcrumbs: BreadcrumbItem[] = [
      {
        label: 'Minutas',
        url: DRAFTS_PAGE_URL,
        clickable: canViewDrafts,
      },
      {
        label: 'Templates de minutas',
        url: draftsTabUrl('models'),
        clickable: false,
      },
    ];
    this.breadcrumbService.setBreadcrumbs(breadcrumbs);
  }

  openWarningModal(id: number) {
    this.loadingVariables = true;
    this.selectedDraft =
      this.drafts.find((draft) => draft.id === id)?.name || '';
    this.warningModal = true;
    this.checkDraftError(id);
  }

  closeWarningModal() {
    this.warningModal = false;
    this.errorVariables = [];
    this.selectedDraft = '';
    this.loadingVariables = false;
  }

  async checkDraftError(id: number) {
    try {
      const res = await this.apiService.get<VariableResponseValues>({
        route: `api/checking_minutes/?document_id=9`,
      });

      this.errorVariables = res.inactive_variables;
    } catch (error) {}

    this.loadingVariables = false;
  }

  async refreshData() {
    await this.changePageData(1);
  }

  async changePageData(page: number) {
    this.loadingDrafts = true;

    await this.fetchDrafts(page, this.buildDraftFilters(this.form.value));
    this.loadingDrafts = false;
  }

  toggleDraftStatus(id: number) {
    const draft = this.drafts.find((draft) => draft.id === id);
    this.selectedModel = draft;
    this.isActive = draft.enabled;
    this.showStatusModal = true;
  }

  async confirmStatusChange() {
    try {
      this.loadingDrafts = true;

      const payload = {
        ...this.selectedModel,
        roles: this.selectedModel.roles.map((role) => role.id),
        type: this.selectedModel.type.id,
        enabled: !this.isActive,
      };

      await this.apiService.patch({
        route: `api/draft/${this.selectedModel.id}/`,
        body: payload,
        token: true,
      });

      this.showStatusModal = false;
      this.fetchData();
      this.toast.show(
        'info',
        'Sucesso',
        `Modelo de minuta ${
          this.isActive ? 'inativado' : 'ativado'
        } com sucesso!`
      );
    } catch (error) {
      console.log(error);
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao tentar alterar o status da minuta.'
      );
    }
    this.loadingDrafts = false;
  }

  closeStatusModal() {
    this.showStatusModal = false;
    this.selectedModel = null;
    this.isActive = false;
  }

  getModalTitle(): string {
    return this.isActive ? 'Inativar minuta' : 'Ativar minuta';
  }

  getModalMessage(): string {
    return `Deseja ${
      this.isActive ? 'inativar' : 'ativar'
    } essa minuta? Essa ação irá ${
      this.isActive ? 'inativar' : 'ativar'
    } o modelo de minuta.`;
  }

  getConfirmText(): string {
    return `Sim, ${this.isActive ? 'inativar' : 'ativar'} minuta`;
  }

  redirectToVariables() {
    this.router.navigateByUrl('/app/drafts/variables');
    this.warningModal = false;
  }

  /**
   * Verifica se os filtros podem ser aplicados
   * Retorna false se houver campos inválidos ou se não houver nenhum filtro preenchido
   */
  get canApplyFilters(): boolean {
    // Verifica se o formulário é válido
    if (this.form.invalid) {
      return false;
    }

    // Verifica se há pelo menos um filtro preenchido
    const values = this.form.value;
    const hasAnyFilter = Object.entries(values).some(([key, value]: any) => {
      // Arrays vazios não contam como filtro
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== null && value !== '' && value !== undefined;
    });

    return hasAnyFilter;
  }
}
